import { Card } from "@/components/ui/card";
import { Target, AlertCircle, Clock, CheckCircle } from "lucide-react";
import { EisenhowerMatrix as EisenhowerData } from "@/types/analytics";
import { motion } from "framer-motion";

interface EisenhowerMatrixProps {
  data: EisenhowerData;
}

export const EisenhowerMatrix = ({ data }: EisenhowerMatrixProps) => {
  const totalTasks = 
    data.urgent.important + 
    data.urgent.notImportant + 
    data.notUrgent.important + 
    data.notUrgent.notImportant;

  const getPercentage = (value: number) => ((value / totalTasks) * 100).toFixed(1);

  const quadrants = [
    {
      title: "Do First",
      description: "Urgent & Important",
      count: data.urgent.important,
      percentage: getPercentage(data.urgent.important),
      icon: AlertCircle,
      color: "bg-red-500",
      textColor: "text-red-500",
      borderColor: "border-red-200",
      recommendation: "Handle these tasks immediately!"
    },
    {
      title: "Schedule",
      description: "Not Urgent but Important",
      count: data.notUrgent.important,
      percentage: getPercentage(data.notUrgent.important),
      icon: Clock,
      color: "bg-blue-500",
      textColor: "text-blue-500",
      borderColor: "border-blue-200",
      recommendation: "Plan these tasks carefully"
    },
    {
      title: "Delegate",
      description: "Urgent but Not Important",
      count: data.urgent.notImportant,
      percentage: getPercentage(data.urgent.notImportant),
      icon: Target,
      color: "bg-yellow-500",
      textColor: "text-yellow-500",
      borderColor: "border-yellow-200",
      recommendation: "Consider delegating these"
    },
    {
      title: "Eliminate",
      description: "Not Urgent & Not Important",
      count: data.notUrgent.notImportant,
      percentage: getPercentage(data.notUrgent.notImportant),
      icon: CheckCircle,
      color: "bg-green-500",
      textColor: "text-green-500",
      borderColor: "border-green-200",
      recommendation: "Consider eliminating these"
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Eisenhower Matrix</h2>
        <Target className="h-5 w-5 text-purple-500" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {quadrants.map((quadrant, index) => {
          const Icon = quadrant.icon;
          return (
            <motion.div
              key={quadrant.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 border rounded-lg ${quadrant.borderColor} hover:shadow-md transition-shadow`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-medium ${quadrant.textColor}`}>
                  {quadrant.title}
                </h3>
                <Icon className={`h-4 w-4 ${quadrant.textColor}`} />
              </div>
              
              <p className="text-sm text-gray-500 mb-2">
                {quadrant.description}
              </p>
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Tasks: {quadrant.count}</span>
                <span className="text-sm text-gray-500">
                  {quadrant.percentage}%
                </span>
              </div>

              <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${quadrant.percentage}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                  className={`h-full rounded-full ${quadrant.color}`}
                />
              </div>

              <p className="text-xs text-gray-500 italic">
                {quadrant.recommendation}
              </p>
            </motion.div>
          );
        })}
      </div>
    </Card>
  );
}; 